import { useState, useEffect } from "react";
import request from "project-config/services/request/request";
import useForm from "antd/es/form/hooks/useForm";
import { VerificationStepsEnum, SyncStatusEnum } from "../constant";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useDingTalkSubAccountStore } from "../../../store/dingtalk-subaccount-store";

export interface IVerificationBackProps {
  aesKey: string;
  token: string;
  callbackUrl: string;
  authCallbackUrl: string;
  appHomepageUrl: string;
  pcHomepageUrl: string;
  enterpriseAlias: string;
  isValidated: number;
}
export interface IVerificationFromProps {
  appKey: string;
  appSecret: string;
}
export interface IDingTalkOrganization {
  id: number | null;
  isLeader: number | null;
  isExist: number | null;
  position: string | null;
  parentId: null;
  deptId: null;
  name: string;
  totalPersonnel: number;
  deptList: IDingTalkOrganization[];
  keyIndex: number;
}

export function useChildIntergrationSettingInfo() {
  const { t } = useTranslation("childManager");
  const [verificationForm] = useForm<IVerificationFromProps>();
  const [verificationBackForm] = useForm<IVerificationBackProps>();
  const [verificatioFormLoading, setVerificatioFormLoading] = useState(false);
  const [saveEnterpriseNameLoading, setSaveEnterpriseNameLoading] =
    useState(false);
  const [verificationBackInfo, setVerificationBackInfo] =
    useState<IVerificationBackProps>();
  const [currentSteps, setCurrentSteps] = useState(VerificationStepsEnum.STEP1);
  const {
    syncTimer,
    queryDingTalkAppSettings,
    queryDeptList,
    setIsSyncDataFail,
    setOpenSettingModal,
    isCheckKeyData,
    credentialsInfo,
    setCurrentBtnStatus,
    setIsSyncData,
  } = useDingTalkSubAccountStore();
  const currentStepForm = {
    [VerificationStepsEnum.STEP1]: verificationForm,
    [VerificationStepsEnum.STEP2]: verificationBackForm,
    [VerificationStepsEnum.STEP3]: verificationBackForm,
  };
  const currentStepBtnLoading = {
    [VerificationStepsEnum.STEP1]: verificatioFormLoading,
    [VerificationStepsEnum.STEP2]: saveEnterpriseNameLoading,
    [VerificationStepsEnum.STEP3]: verificatioFormLoading,
  };

  const handleCheckValidate = async () => {
    if (!verificationForm) return;
    setVerificatioFormLoading(true);
    verificationForm
      .validateFields()
      .then(async () => {
        const { data, code } = await request.post(
          "/api/rendering/user/validateConfigInfoInput",
          {
            appKey: verificationForm.getFieldValue("appKey"),
            appSecret: verificationForm.getFieldValue("appSecret"),
          }
        );
        setVerificatioFormLoading(false);
        if (code !== 200 || !data) {
          if (code === 1314)
            message.error(t("dingtalk_subaccount_setting_illegal_error1"));
          if (code === 605)
            message.error(t("dingtalk_subaccount_setting_illegal_error2"));
          return;
        }
        setCurrentSteps(VerificationStepsEnum.STEP2);
        verificationBackForm.setFieldsValue(data);
        setVerificationBackInfo(data);
      })
      .catch(() => {});
  };

  const handleSaveEnterpriseName = async () => {
    if (!verificationBackForm) return;
    setSaveEnterpriseNameLoading(true);
    verificationBackForm
      .validateFields()
      .then(async () => {
        await request.post("/api/rendering/user/setEnterpriseAlias", {
          enterpriseAlias:
            verificationBackForm.getFieldValue("enterpriseAlias"),
        });
        setSaveEnterpriseNameLoading(false);
        setCurrentSteps(VerificationStepsEnum.STEP3);
      })
      .catch(() => {
        setSaveEnterpriseNameLoading(false);
      });
  };

  const checkSyncStatus = async () => {
    const { data, code } = await request.post(
      "/api/rendering/user/getDingTalkDataSyncStatus",
      {}
    );
    if (code !== 200) return;
    if (
      syncTimer.current &&
      [SyncStatusEnum.FAIL, SyncStatusEnum.SUCCESS].includes(data.isSync)
    ) {
      setCurrentSteps(VerificationStepsEnum.STEP3);
      setVerificatioFormLoading(false);
      clearInterval(syncTimer.current);
      setIsSyncData(false);
    }
    if (data.isSync === SyncStatusEnum.FAIL) {
      setIsSyncDataFail(true);
    }
    if (data.isSync === SyncStatusEnum.SUCCESS) {
      setOpenSettingModal(false);
      await queryDingTalkAppSettings();
      queryDeptList();
      message.success(t("dingtalk_subaccount_setting_synchronous_success"));
    }
  };

  const handleSynchronousData = async () => {
    const { code } = await request.post(
      "/api/rendering/user/syncDingTalkData",
      {}
    );
    if (code === 200) {
      // 因为同步需要时间，此时需要轮询查当前同步状，同步成功才会自动关闭弹窗
      setIsSyncData(true);
      setIsSyncDataFail(false);
      setVerificatioFormLoading(true);
      syncTimer.current = setInterval(() => {
        checkSyncStatus();
      }, 5000);
    }
  };

  const getCurrentStepsPre = async () => {
    if (
      [VerificationStepsEnum.STEP1, VerificationStepsEnum.STEP2].includes(
        currentSteps
      )
    ) {
      setCurrentSteps(VerificationStepsEnum.STEP1);
      verificationForm.resetFields();
      setOpenSettingModal(false);
      await queryDingTalkAppSettings();
      queryDeptList();
      return;
    }
    setCurrentBtnStatus(true);
    setCurrentSteps(VerificationStepsEnum.STEP2);
  };

  const getCurrentStepsNext = async () => {
    if (currentSteps === VerificationStepsEnum.STEP1) {
      await handleCheckValidate();
      return;
    }
    if (currentSteps === VerificationStepsEnum.STEP2) {
      await handleSaveEnterpriseName();
      setCurrentBtnStatus(true);
      return;
    }
    await handleSynchronousData();
  };

  const handleCopyText = (copyText: string) => {
    let el = document.createElement("textarea");
    el.value = copyText;
    document.body.appendChild(el);
    el.focus();
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    message.success(t("dingtalk_subaccount_setting_copy_success"));
  };

  useEffect(() => {
    if (!isCheckKeyData) return;
    setCurrentSteps(VerificationStepsEnum.STEP3);
    verificationBackForm.setFieldsValue(credentialsInfo);
    setCurrentBtnStatus(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckKeyData, credentialsInfo]);

  return {
    verificationForm,
    verificationBackInfo,
    handleCheckValidate,
    handleSaveEnterpriseName,
    handleSynchronousData,
    verificatioFormLoading,
    saveEnterpriseNameLoading,
    verificationBackForm,
    currentSteps,
    setCurrentSteps,
    getCurrentStepsPre,
    getCurrentStepsNext,
    currentStepForm,
    currentStepBtnLoading,
    handleCopyText,
  };
}
